import React, { useState, useEffect } from "react";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableContainer,
  Paper,
  Pagination,
  Typography,  
} from "@mui/material";
import PaginationItem from "@mui/material/PaginationItem";
import moment from "moment";
import { styled } from "@mui/material/styles";
import { updateEditorPayment, getEditorAndProjectList } from "../../apis/apiList/editorPaymentApi";
import ClipLoader from "react-spinners/ClipLoader";
import theme from "../../theme/theme";
import { useMessage } from "../../context/MessageContext";
import { useNavigate } from "react-router-dom";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#495967",
    color: theme.palette.common.white,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const CustomPaginationItem = styled(PaginationItem)(({ theme, selected }) => ({
  backgroundColor: selected ? theme.palette.primary.main : "#495967",
  color: "#fff",
  borderRadius: "0px",
  fontWeight: selected ? "bold" : "normal",
  margin: "0 4px",
  maxHeight: "26px",
  padding: "0px",
  "&:hover": {
    backgroundColor: selected ? "#d40000" : "#1e2a33",
  },
  "&.Mui-selected": {
    backgroundColor: selected ? theme.palette.primary.main : "#495967",
    color: "#fff",
    padding: "0px",
  },
}));

const statusOptions = ['Completed', 'Active', 'Pending Approval', 'Not Started'];

function EditorPaymentList({ filters, editorId, userDetails, handleTotalAmount, handleFilterTotalAmount }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const { showMessage } = useMessage();
  const navigate = useNavigate();

  const rowsPerPage = 6;

  useEffect(() => {
    fetchProjectData();
  }, [filters, editorId]);

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const response = await getEditorAndProjectList(editorId, {
        params: filters,
        header: {
          'Content-Type': 'application/json',
          'x-access-token': userDetails.jwtToken,
        },
      });
      setProjects(response.projects || []);
      handleFilterTotalAmount(response);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
      showMessage("Failed to fetch projects", "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects;
  const paginatedProjects = filteredProjects.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const stageLabels = {
    scriptsAndVisuals: 'Scripts & Visuals',
    voiceOver: 'Voice Over',
    video: 'Video',
  };

  function getLatestApprovalStage(approvals) {
    if (!approvals) return "No approval data";
    const stages = Object.keys(stageLabels);
    const allApproved = stages.every((stage) => approvals[stage]);
    if (allApproved) return "Approved";
    for (let i = 0; i < stages.length; i++) {
      const stage = stages[i];
      if (!approvals[stage]) {
        return stageLabels[stage];
      }
    }
    return "No stage in progress";
  }

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Box>
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="payment table">
          <TableHead>
            <TableRow>
              {/* <StyledTableCell>id</StyledTableCell> */}
              <StyledTableCell>Project Name</StyledTableCell>
              <StyledTableCell>Organization</StyledTableCell>
              <StyledTableCell align="left">Stage</StyledTableCell>
              <StyledTableCell align="left">Status</StyledTableCell>
              <StyledTableCell align="left">Amount</StyledTableCell>
              <StyledTableCell align="left" sx={{ paddingLeft: '48px' }}>Paid</StyledTableCell>
              <StyledTableCell align="left">Due Date</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <StyledTableRow>
                <StyledTableCell colSpan={6} align="center">
                  <ClipLoader size={30} color={theme.palette.primary.main} />
                </StyledTableCell>
              </StyledTableRow>
            ) : paginatedProjects.length > 0 ? (
              paginatedProjects.map((project, i) => (
                <StyledTableRow key={i}>
                  {/* <StyledTableCell component="th" scope="row">
                    {project.projectData?._id}
                  </StyledTableCell> */}
                  <StyledTableCell component="th" scope="row" onClick={() => window.open(`/org/${project.organizationData?._id}/project/view/${project.projectData?._id}`, '_blank')} style={{ cursor: 'pointer' }}>
                    {project.projectData?.projectName || "N/A"}
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row" onClick={() => window.open(`/org/${project.organizationData?._id}/projects`, '_blank')} style={{ cursor: 'pointer' }}>
                    {project.organizationData?.organizationName || "N/A"}
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    {getLatestApprovalStage(project.projectData?.approvals)}
                  </StyledTableCell>
                  <StyledTableCell align="left" sx={{ textTransform: "capitalize" }}>
                    {project.projectData?.status || "N/A"}
                  </StyledTableCell>
                  <StyledTableCell align="left">${project.amount}</StyledTableCell>
                  <StyledTableCell align="left">
                    <Box
                      sx={{
                        backgroundColor: project.isPaid ? "#d4edda" : "#fff3cd",
                        border: project.isPaid ? "2px solid #c3e6cb" : "2px solid #ffeeba",
                        borderRadius: "16px",
                        padding: "4.5px 0px 4.5px 5px !important",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        width: "100px",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          textAlign: "center",
                          fontWeight: 600,
                          color: project.isPaid ? "#155724" : "#856404",
                        }}
                      >
                        {project.isPaid ? "Paid" : "Pending"}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    {project.projectData?.dueDate
                      ? moment(project.projectData.dueDate).format("MM/DD/YYYY")
                      : "TBD"}
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={6} align="center">
                  No project records found.
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Pagination
        count={Math.ceil(filteredProjects.length / rowsPerPage)}
        page={page}
        onChange={handlePageChange}
        renderItem={(item) => <CustomPaginationItem {...item} />}
        siblingCount={1}
        boundaryCount={1}
        showFirstButton
        showLastButton
      />

    </Box>
  );
}

export default EditorPaymentList;
